import { ChangeEvent } from 'react'
import { Period } from '../../../types/definitions/Period'
import DashboardSearchInput, { DASHBOARD_FIELD_BASE } from './DashboardSearchInput'
import ExportCsvButton from './ExportCsvButton'

export type ReviewsFiltersIdPrefix = 'completed-reviews' | 'skipped-reviews'

type Props = {
  idPrefix: ReviewsFiltersIdPrefix
  periods: Period[]
  periodId: number | undefined
  search: string
  reviewerName: string
  reviewerId: number | null
  reviewerPillName: string | null
  onPeriodChange: (periodId: number | undefined) => void
  onSearchChange: (value: string) => void
  onReviewerNameChange: (value: string) => void
  onClearReviewer: () => void
  onExport: () => Promise<void>
  exportDisabled?: boolean
}

const ReviewsFilters = ({
  idPrefix,
  periods,
  periodId,
  search,
  reviewerName,
  reviewerId,
  reviewerPillName,
  onPeriodChange,
  onSearchChange,
  onReviewerNameChange,
  onClearReviewer,
  onExport,
  exportDisabled,
}: Props): JSX.Element => {
  const periodSelectId = `${idPrefix}-period`
  const searchId = `${idPrefix}-search`
  const reviewerNameId = `${idPrefix}-reviewer-name`

  const handlePeriodChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    onPeriodChange(value === '' ? undefined : Number(value))
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor={periodSelectId} className="text-xl font-medium text-blue-gray">
            Period
          </label>
          <select
            id={periodSelectId}
            value={periodId ?? ''}
            onChange={handlePeriodChange}
            className={`${DASHBOARD_FIELD_BASE} px-3`}
          >
            <option value="">All periods</option>
            {periods.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <DashboardSearchInput
          id={searchId}
          value={search}
          onChange={onSearchChange}
          placeholder="Employee name or email…"
          ariaLabel="Search by employee name or email"
          className="w-80"
        />

        <DashboardSearchInput
          id={reviewerNameId}
          value={reviewerName}
          onChange={onReviewerNameChange}
          placeholder="Reviewer name…"
          ariaLabel="Filter by reviewer name"
          className="w-64"
        />

        {reviewerId !== null && (
          <span className="inline-flex items-center gap-2 rounded-full border border-soft-gray bg-just-gray px-3 py-1 text-xl text-blue-gray">
            <span className="font-medium">{reviewerPillName ?? 'Reviewer filter active'}</span>
            <button
              type="button"
              onClick={onClearReviewer}
              aria-label="Clear reviewer filter"
              className="text-stone-gray hover:text-dark-blue"
            >
              ×
            </button>
          </span>
        )}
      </div>

      <ExportCsvButton onExport={onExport} disabled={exportDisabled} />
    </div>
  )
}

export default ReviewsFilters
